import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import {
  Building2,
  User,
  Mail,
  Lock,
  Phone,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Loader2,
  Shield,
  Gift,
  AlertTriangle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; // Test key for development

// Blocked email domains
const BLOCKED_DOMAINS = [
  "xmail.ru", "mail.ru", "yandex.ru", "rambler.ru", "inbox.ru",
  "tempmail.com", "guerrillamail.com", "10minutemail.com", "throwaway.email",
  "mailinator.com", "fakeinbox.com", "tempail.com", "temp-mail.org",
  "disposablemail.com", "trashmail.com", "getnada.com", "maildrop.cc",
  "sharklasers.com", "spam4.me", "grr.la", "emailondeck.com",
  "mohmal.com", "dispostable.com", "mintemail.com", "tempr.email",
  "yopmail.com", "crazymailing.com", "tempinbox.com", "emailfake.com",
];

const PublicSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = form, 2 = success
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Company data
    business_name: "",
    phone: "",
    // Admin user data
    admin_name: "",
    admin_email: "",
    admin_password: "",
    admin_password_confirm: "",
  });

  const [errors, setErrors] = useState({});

  const isBlockedDomain = (email) => {
    const domain = email.toLowerCase().split("@")[1];
    if (!domain) return false;
    
    // Check blocked domains
    if (BLOCKED_DOMAINS.includes(domain)) return true;
    
    // Check suspicious patterns
    const suspiciousPatterns = ["temp", "fake", "spam", "trash", "disposable", "throw"];
    return suspiciousPatterns.some(pattern => domain.includes(pattern));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.business_name.trim()) {
      newErrors.business_name = "El nombre de la empresa es requerido";
    } else if (formData.business_name.trim().length < 3) {
      newErrors.business_name = "El nombre debe tener al menos 3 caracteres";
    }
    
    // Phone is now required
    const cleanPhone = formData.phone?.replace(/[\s\-\(\)]/g, "") || "";
    if (!cleanPhone || cleanPhone.length < 10) {
      newErrors.phone = "El teléfono es requerido (mínimo 10 dígitos)";
    }
    
    if (!formData.admin_name.trim()) {
      newErrors.admin_name = "Tu nombre es requerido";
    }
    
    if (!formData.admin_email.trim()) {
      newErrors.admin_email = "El correo es requerido";
    } else if (!/\S+@\S+\.\S+/.test(formData.admin_email)) {
      newErrors.admin_email = "Correo inválido";
    } else if (isBlockedDomain(formData.admin_email)) {
      newErrors.admin_email = "Este dominio de correo no está permitido. Usa un correo corporativo o personal válido.";
    }
    
    if (!formData.admin_password) {
      newErrors.admin_password = "La contraseña es requerida";
    } else if (formData.admin_password.length < 6) {
      newErrors.admin_password = "Mínimo 6 caracteres";
    }
    
    if (formData.admin_password !== formData.admin_password_confirm) {
      newErrors.admin_password_confirm = "Las contraseñas no coinciden";
    }
    
    if (!acceptTerms) {
      newErrors.terms = "Debes aceptar los términos";
    }
    
    // CAPTCHA validation
    if (!captchaToken) {
      newErrors.captcha = "Por favor completa la verificación CAPTCHA";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .substring(0, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const slug = generateSlug(formData.business_name);
      
      const response = await axios.post(`${API_URL}/api/public/signup`, {
        company: {
          business_name: formData.business_name.toUpperCase(),
          slug: slug,
          phone: formData.phone,
        },
        admin: {
          name: formData.admin_name,
          email: formData.admin_email.toLowerCase(),
          password: formData.admin_password,
        },
        captcha_token: captchaToken
      });
      
      if (response.data.success) {
        setStep(2);
        toast.success("¡Cuenta creada exitosamente!");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al crear la cuenta";
      toast.error(message);
      
      // Reset captcha on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
      
      if (message.includes("correo") || message.includes("dominio")) {
        setErrors({ admin_email: message });
      } else if (message.includes("empresa") || message.includes("slug")) {
        setErrors({ business_name: "Este nombre de empresa ya está registrado" });
      } else if (message.includes("teléfono") || message.includes("telefono")) {
        setErrors({ phone: message });
      } else if (message.includes("CAPTCHA")) {
        setErrors({ captcha: message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    const slug = generateSlug(formData.business_name);
    navigate(`/empresa/${slug}/login`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            CIA Servicios®
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {step === 1 ? "Crea tu cuenta gratis" : "¡Bienvenido!"}
          </h1>
          <p className="text-blue-200">
            {step === 1 
              ? "Comienza con 7 días de prueba sin costo"
              : "Tu cuenta ha sido creada exitosamente"
            }
          </p>
        </div>

        {step === 1 ? (
          <Card className="shadow-2xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <CardTitle>Registro de Empresa</CardTitle>
              <CardDescription>
                Completa tus datos para comenzar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Company Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Building2 className="h-4 w-4" />
                    Datos de la Empresa
                  </div>
                  
                  <div>
                    <Label htmlFor="business_name">Nombre de la Empresa *</Label>
                    <Input
                      id="business_name"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Mi Empresa S.A. de C.V."
                      className={errors.business_name ? "border-red-500" : ""}
                    />
                    {errors.business_name && (
                      <p className="text-xs text-red-500 mt-1">{errors.business_name}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      Teléfono *
                    </Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="33 1234 5678"
                      className={errors.phone ? "border-red-500" : ""}
                    />
                    {errors.phone && (
                      <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>

                <hr className="my-4" />

                {/* Admin User Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <User className="h-4 w-4" />
                    Tu Cuenta de Administrador
                  </div>
                  
                  <div>
                    <Label htmlFor="admin_name">Tu Nombre *</Label>
                    <Input
                      id="admin_name"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      placeholder="Juan Pérez"
                      className={errors.admin_name ? "border-red-500" : ""}
                    />
                    {errors.admin_name && (
                      <p className="text-xs text-red-500 mt-1">{errors.admin_name}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="admin_email" className="flex items-center gap-2">
                      <Mail className="h-3 w-3" />
                      Correo Electrónico *
                    </Label>
                    <Input
                      id="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      placeholder="tu@email.com"
                      className={errors.admin_email ? "border-red-500" : ""}
                    />
                    {errors.admin_email && (
                      <p className="text-xs text-red-500 mt-1">{errors.admin_email}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="admin_password" className="flex items-center gap-2">
                        <Lock className="h-3 w-3" />
                        Contraseña *
                      </Label>
                      <Input
                        id="admin_password"
                        type="password"
                        value={formData.admin_password}
                        onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                        placeholder="••••••"
                        className={errors.admin_password ? "border-red-500" : ""}
                      />
                      {errors.admin_password && (
                        <p className="text-xs text-red-500 mt-1">{errors.admin_password}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="admin_password_confirm">Confirmar *</Label>
                      <Input
                        id="admin_password_confirm"
                        type="password"
                        value={formData.admin_password_confirm}
                        onChange={(e) => setFormData({ ...formData, admin_password_confirm: e.target.value })}
                        placeholder="••••••"
                        className={errors.admin_password_confirm ? "border-red-500" : ""}
                      />
                      {errors.admin_password_confirm && (
                        <p className="text-xs text-red-500 mt-1">{errors.admin_password_confirm}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Trial Badge */}
                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Gift className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">7 días de prueba gratis</p>
                      <p className="text-xs text-green-600">Sin tarjeta de crédito requerida</p>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="terms"
                    checked={acceptTerms}
                    onCheckedChange={setAcceptTerms}
                    className={errors.terms ? "border-red-500" : ""}
                  />
                  <Label htmlFor="terms" className="text-sm text-slate-600 leading-tight cursor-pointer">
                    Acepto los{" "}
                    <a href="/terminos" className="text-blue-600 hover:underline">
                      términos y condiciones
                    </a>{" "}
                    y la{" "}
                    <a href="/privacidad" className="text-blue-600 hover:underline">
                      política de privacidad
                    </a>
                  </Label>
                </div>
                {errors.terms && (
                  <p className="text-xs text-red-500">{errors.terms}</p>
                )}

                {/* reCAPTCHA */}
                <div className="flex flex-col items-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    hl="es"
                  />
                  {errors.captcha && (
                    <p className="text-xs text-red-500 mt-2">{errors.captcha}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  disabled={loading || !captchaToken}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Crear mi cuenta gratis
                </Button>

                <p className="text-center text-sm text-slate-500">
                  ¿Ya tienes cuenta?{" "}
                  <Link to="/" className="text-blue-600 hover:underline">
                    Inicia sesión
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        ) : (
          /* Success Step */
          <Card className="shadow-2xl border-0">
            <CardContent className="p-8 text-center">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                ¡Cuenta creada exitosamente!
              </h2>
              
              <p className="text-slate-600 mb-6">
                Tu período de prueba de <strong>7 días</strong> ha comenzado.
                Ahora puedes iniciar sesión y configurar tu empresa.
              </p>
              
              <div className="space-y-3 text-left bg-slate-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Empresa: <strong>{formData.business_name}</strong></span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Email: <strong>{formData.admin_email}</strong></span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <span className="text-sm">Rol: <strong>Administrador</strong></span>
                </div>
              </div>
              
              <Button
                onClick={handleGoToLogin}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Iniciar sesión
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-blue-200/60 text-sm mt-6">
          © 2026 CIA Servicios®. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default PublicSignup;
